// window.__game og URL-parametre (DESIGN.md: Testbarhed). Kun tynde kald ind i spillets egne funktioner.
//
// URL: ?seed ?level ?difficulty ?autoplay=novice|average|expert ?quality=low|mid|high
//      ?renderer=webgl|webgpu ?mute ?p2=volt|bot ?agent=mock

import type { AutoplayModel } from './ai/autoplay';
import { isAutoplayModel } from './ai/autoplay';
import type { RendererPref } from './render/app';
import type { ActionKind, DifficultyId, Intent, Summary, ThreatKind } from './sim';
import { DIFFICULTY_ORDER } from './sim';

export interface DebugState extends Summary {
  fps: number;
  autoplay: AutoplayModel | null;
  screen: string | null;
  paused: boolean;
  /** Står solo stille og venter på spilleren (årsskifte, træk i gang)? */
  held: boolean;
  /** Går tiden faktisk (ingen pause, intet held, intet kort, ingen skærm der holder)? */
  running: boolean;
  speed: number;
  quality: string;
  renderer: string;
  /** Spillerens plads (p1 planlægger, p2 driftsvagt) og om rollerne gælder. */
  player: 'p1' | 'p2';
  roles: boolean;
  /** Frekvensnålen (spor tillid): mHz fra 50,00 Hz og antal tryk. null uden roller. */
  hz: { dev: number; trims: number; earned: number; paid: number } | null;
  /** Lad AI spille: antal udførte træk og det meldte træk. null, når spilleren selv har roret. */
  pilot: { moves: number; next: string | null } | null;
}

export interface GameApi {
  /** JSON-resumé af spillet lige nu. */
  state(): DebugState;
  /** Start en bane direkte (uden forside og banestart). */
  start(level?: string, difficulty?: DifficultyId, seed?: number): void;
  intent(i: Partial<Intent> & { kind: ActionKind }): void;
  /** Spol simulationen frem uden at vente på rigtig tid. Stopper ved banens slutning. */
  step(seconds: number): Summary;
  give(kr: number): void;
  event(kind: ThreatKind): void;
  /** Spøgelsesspiller (testkrog): følger trænerens råd som et menneske, hintet bliver tændt. */
  ghost(on: boolean | 'lazy' | 'both'): void;
  /** Slå autoplay til (novice, average, expert) eller fra (null). */
  autoplay(model: AutoplayModel | null): void;
  /** Nodens midte i sidens koordinater (til at trække med musen i tests). */
  nodeScreen(id: string): { x: number; y: number } | null;
  lineScreen(id: string): { x: number; y: number } | null;
  threatScreen(id: string): { x: number; y: number } | null;
  /** Vis byens kort, som et tryk på byen ville (når et tryk rammer en ledning eller stationsringen). */
  inspect(id: string): void;
  /** Fyld nodens pladser med kopier af en af dens ledninger (testkrog: en fuld nabo). */
  fill(id: string): boolean;
  pause(on: boolean): void;
  speed(n: 1 | 2): void;
  /** Banernes id i kampagnens rækkefølge. */
  levels(): string[];
  /** Vælg pakke ved årsskifte. */
  pick(i: number): void;
}

export interface UrlParams {
  level: string;
  difficulty: DifficultyId;
  seed: number;
  autoplay: AutoplayModel | null;
  quality: string | null;
  renderer: RendererPref;
  mute: boolean;
  /** Banen eller autoplay står i URL'en: spring forsiden over. */
  direct: boolean;
  /** Makker på p2: Volt (AI) eller kun reservebotten. */
  p2: 'volt' | 'bot' | null;
  /** mock: deterministiske Volt-svar uden net. */
  agent: 'mock' | 'live';
}

export function readParams(search: string): UrlParams {
  const q = new URLSearchParams(search);
  const d = q.get('difficulty') as DifficultyId | null;
  const seed = Number(q.get('seed'));
  const ap = q.get('autoplay');
  const quality = q.get('quality');
  return {
    level: q.get('level') ?? '01-foerste-vind',
    difficulty: d && DIFFICULTY_ORDER.includes(d) ? d : 'laerling',
    seed: Number.isFinite(seed) && seed > 0 ? Math.floor(seed) : Math.floor(Math.random() * 1_000_000) + 1,
    // ?autoplay uden værdi betyder en gennemsnitlig spiller.
    autoplay: q.has('autoplay') ? (isAutoplayModel(ap) ? ap : 'average') : null,
    quality: quality === 'low' || quality === 'mid' || quality === 'high' ? quality : null,
    renderer: q.get('renderer') === 'webgpu' ? 'webgpu' : 'webgl',
    mute: q.has('mute') && q.get('mute') !== '0',
    direct: q.has('level') || q.has('autoplay'),
    p2: q.get('p2') === 'volt' ? 'volt' : q.get('p2') === 'bot' ? 'bot' : null,
    agent: q.get('agent') === 'mock' ? 'mock' : 'live',
  };
}

export function install(api: GameApi): void {
  (window as unknown as { __game: GameApi }).__game = api;
}
